import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login, register, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [googleOnlyEmail, setGoogleOnlyEmail] = useState(null);  // email that only has a Google account

  // If already logged in, redirect (must be in useEffect, NOT during render)
  useEffect(() => {
    if (user) navigate('/account', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGoogleOnlyEmail(null);
    if (isRegister && form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        await register(form.name, form.email, form.password);
        toast.success('Account created!');
      } else {
        await login(form.email, form.password);
        toast.success('Welcome back!');
      }
      // Navigation is handled by the useEffect above when `user` state changes.
      // Calling navigate() here too causes a DataCloneError (double-navigate race).
    } catch (err) {
      if (err?.code === 'google_account_no_password') {
        setGoogleOnlyEmail(form.email);
      } else {
        const msg = typeof err?.message === 'string' ? err.message : 'Something went wrong. Please try again.';
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const sendSetPasswordEmail = async () => {
    if (!googleOnlyEmail) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: googleOnlyEmail }),
      });
      if (res.ok) {
        toast.success(`Email sent to ${googleOnlyEmail}. Check your inbox for a link to set a password.`);
        setGoogleOnlyEmail(null);
      } else {
        toast.error('Could not send email. Please try again.');
      }
    } catch {
      toast.error('Could not send email. Please try again.');
    }
  };

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="login-page">
      <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight" data-testid="login-title">
            {isRegister ? 'Create Account' : 'Sign In'}
          </h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">
            {isRegister ? 'Join Boss Cushions to track your orders and save your details.' : 'Sign in to manage your orders and account.'}
          </p>
        </div>
      </section>

      <div className="flex items-center justify-center px-4 py-6 sm:py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-card p-6 sm:p-8">

          {googleOnlyEmail && (
            <div className="mb-5 rounded-xl border border-teal-200 bg-teal-50/60 p-4" data-testid="google-account-prompt">
              <p className="text-sm font-semibold text-teal-900">You already have an account with Google</p>
              <p className="text-xs text-teal-800/80 mt-1 leading-relaxed">
                <span className="font-medium">{googleOnlyEmail}</span> was registered using Google Sign-In — there's no password on it yet.
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <Button
                  type="button"
                  onClick={loginWithGoogle}
                  className="w-full h-10 bg-bush hover:bg-bush/90 text-white font-medium"
                  data-testid="google-only-signin-btn"
                >
                  Sign in with Google
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={sendSetPasswordEmail}
                  className="w-full h-10 border-teal-300 text-teal-800 hover:bg-teal-50 hover:text-teal-800 font-medium"
                  data-testid="google-only-set-password-btn"
                >
                  Email me a link to set a password
                </Button>
              </div>
              <button
                type="button"
                onClick={() => setGoogleOnlyEmail(null)}
                className="text-[11px] text-teal-700/70 hover:text-teal-900 mt-3 underline"
                data-testid="google-only-dismiss"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Google Sign In */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-11 mb-4 border-gray-300 font-medium"
            onClick={loginWithGoogle}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-gray-400">or</span></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label className="text-sm font-medium">Full Name</Label>
                <Input
                  type="text" required placeholder="John Doe"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 h-11" data-testid="register-name-input"
                />
              </div>
            )}
            <div>
              <Label className="text-sm font-medium">Email</Label>
              <Input
                type="email" required placeholder="you@email.com"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 h-11" data-testid="login-email-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Password</Label>
              <Input
                type="password" required placeholder="Min 6 characters"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 h-11" data-testid="login-password-input"
              />
            </div>
            {isRegister && (
              <div>
                <Label className="text-sm font-medium">Confirm Password</Label>
                <Input
                  type="password" required placeholder="Confirm your password"
                  value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  className="mt-1 h-11" data-testid="register-confirm-password-input"
                />
              </div>
            )}
            <Button type="submit" className="w-full h-11 bg-bush hover:bg-bush/90 text-white font-medium" disabled={loading} data-testid="login-submit-btn">
              {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
            </Button>
            {!isRegister && (
              <div className="text-right">
                <Link to="/forgot-password" className="text-sm text-bush hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            )}
          </form>

          <p className="text-sm text-center mt-5 text-gray-500">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button onClick={() => setIsRegister(!isRegister)} className="text-bush font-medium hover:underline" data-testid="toggle-auth-mode">
              {isRegister ? 'Sign In' : 'Create Account'}
            </button>
          </p>
        </div>

        {/* Order tracking link */}
        <div className="mt-4 text-center">
          <Link to="/track-order" className="text-sm text-bush hover:underline" data-testid="track-order-link">
            Track an order without an account
          </Link>
        </div>
      </div>
      </div>
    </div>
  );
}
